import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Switch, FormControlLabel, Alert, CircularProgress, Divider } from '@mui/material';
import api from '../../api';
import { getDashboardFlyerUrl } from '../../utils/dashboardFlyerUrl';

const defaultIndividualAccess = {
    jamb: 0,
    waec: 0,
    jambMockTest: 0,
    waecMockTest: 0,
    jambResultCheck: 0,
    waecResultCheck: 0,
    labs: 0,
    languageHub: 0,
};

const defaultReferralProgram = {
    enabled: true,
    welcomeDiscountPercent: 5,
    welcomeDiscountValidityDays: 14,
    referralRewardPercent: 5,
    referralRewardValidityDays: 60,
    milestones: [1, 3, 5],
    maxRewardsPerReferral: 3,
};

const adminRequestConfig = () => {
    const token = localStorage.getItem('admin_auth_token');
    return token ? { headers: { 'x-auth-token': token } } : undefined;
};

const getRequestErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        if (response && typeof response === 'object' && 'data' in response) {
            const data = response.data;
            if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
                return data.message;
            }
        }
    }
    return fallback;
};

const AdminSiteSettingsTab: React.FC = () => {
    const [marqueeText, setMarqueeText] = useState('');
    const [marqueeEnabled, setMarqueeEnabled] = useState(true);
    const [dashboardAnnouncementText, setDashboardAnnouncementText] = useState('');
    const [dashboardPostEnabled, setDashboardPostEnabled] = useState(false);
    const [dashboardPostTitle, setDashboardPostTitle] = useState('');
    const [dashboardPostBody, setDashboardPostBody] = useState('');
    const [dashboardPostImageUrl, setDashboardPostImageUrl] = useState('');
    const [dashboardPostHtmlUrl, setDashboardPostHtmlUrl] = useState('');
    const [individualAccess, setIndividualAccess] = useState(defaultIndividualAccess);
    const [referralProgram, setReferralProgram] = useState(defaultReferralProgram);
    const [schoolPerStudentRate, setSchoolPerStudentRate] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPostAsset, setUploadingPostAsset] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const res = await api.get('/site-settings', adminRequestConfig());
            if (res.data?.data) {
                setMarqueeText(res.data.data.marqueeText || '');
                setMarqueeEnabled(res.data.data.marqueeEnabled ?? true);
                setDashboardAnnouncementText(res.data.data.dashboardAnnouncementText || '');
                setDashboardPostEnabled(res.data.data.dashboardPost?.enabled ?? false);
                setDashboardPostTitle(res.data.data.dashboardPost?.title || '');
                setDashboardPostBody(res.data.data.dashboardPost?.body || '');
                setDashboardPostImageUrl(res.data.data.dashboardPost?.imageUrl || '');
                setDashboardPostHtmlUrl(res.data.data.dashboardPost?.htmlUrl || '');
                setIndividualAccess({
                    ...defaultIndividualAccess,
                    ...(res.data.data.individualAccess || {})
                });
                setSchoolPerStudentRate(Number(res.data.data.schoolAccess?.perStudent ?? 0));
                setReferralProgram({ ...defaultReferralProgram, ...(res.data.data.referralProgram || {}) });
            }
        } catch (err) {
            setError('Failed to load site settings.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await api.put('/site-settings', {
                marqueeText,
                marqueeEnabled,
                dashboardAnnouncementText,
                individualAccess,
                referralProgram,
                schoolAccess: { perStudent: schoolPerStudentRate }
            }, adminRequestConfig());
            setSuccess('Site settings updated successfully!');
        } catch (err) {
            setError('Failed to update site settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDashboardPost = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await api.put('/site-settings', {
                dashboardPost: {
                    enabled: dashboardPostEnabled,
                    title: dashboardPostTitle,
                    body: dashboardPostBody,
                    imageUrl: dashboardPostImageUrl,
                    htmlUrl: dashboardPostHtmlUrl,
                },
            }, adminRequestConfig());
            setSuccess('Student dashboard post saved.');
        } catch (err) {
            setError(getRequestErrorMessage(err, 'Failed to save the student dashboard post.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDashboardPost = async () => {
        if (!window.confirm('Delete the dashboard announcement for all students?')) return;
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            await api.delete('/site-settings/dashboard-post', adminRequestConfig());
            setDashboardPostEnabled(false);
            setDashboardPostTitle('');
            setDashboardPostBody('');
            setDashboardPostImageUrl('');
            setDashboardPostHtmlUrl('');
            setSuccess('Dashboard announcement deleted.');
        } catch (err) {
            setError('Failed to delete the dashboard announcement.');
        } finally {
            setSaving(false);
        }
    };

    const handleDashboardPostAssetUpload = async (file?: File) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setError('Announcement files must be 5 MB or smaller.');
            return;
        }

        const isHtml = file.name.toLowerCase().endsWith('.html') && file.type === 'text/html';
        const isImage = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type);
        if (!isHtml && !isImage) {
            setError('Choose a JPEG, PNG, GIF, WebP, or HTML file.');
            return;
        }

        try {
            setUploadingPostAsset(true);
            setError(null);
            setSuccess(null);
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/uploads/dashboard-announcement', formData, {
                ...adminRequestConfig(),
                headers: {
                    ...adminRequestConfig()?.headers,
                    'Content-Type': 'multipart/form-data',
                },
            });
            const fileUrl = response.data?.data?.fileUrl;
            if (typeof fileUrl !== 'string' || !fileUrl.trim()) {
                throw new Error('Upload response did not include an announcement file URL.');
            }
            if (isHtml) {
                setDashboardPostHtmlUrl(fileUrl);
                setDashboardPostImageUrl('');
            } else {
                setDashboardPostImageUrl(fileUrl);
                setDashboardPostHtmlUrl('');
            }
            setSuccess('Announcement file uploaded and stored successfully.');
        } catch {
            setError('Failed to upload the announcement file.');
        } finally {
            setUploadingPostAsset(false);
        }
    };

    const updateIndividualAccess = (key: keyof typeof defaultIndividualAccess, value: string) => {
        const numericValue = Number(value || 0);
        setIndividualAccess(prev => ({ ...prev, [key]: Number.isFinite(numericValue) ? numericValue : 0 }));
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 900 }}>
            <Typography variant="h5" gutterBottom>Global Site Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure the dynamic properties of the public website globally and define the required access fees for premium features.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControlLabel
                    control={
                        <Switch 
                            checked={marqueeEnabled} 
                            onChange={(e) => setMarqueeEnabled(e.target.checked)} 
                            color="primary" 
                        />
                    }
                    label="Enable Dynamic Public Marquee Banner"
                />

                <TextField
                    label="Marquee Announcement Text"
                    value={marqueeText}
                    onChange={(e) => setMarqueeText(e.target.value)}
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    disabled={!marqueeEnabled}
                    helperText={!marqueeEnabled ? 'Enable the marquee to edit text' : 'This text will scroll infinitely at the highest top layer of the application structure.'}
                />

                <Divider />

                <Box component="section" aria-labelledby="dashboard-announcements-heading" sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                    <Typography id="dashboard-announcements-heading" variant="h6" gutterBottom>Dashboard Announcements</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This message appears in the student dashboard Announcements section. It is separate from the scrolling marquee and the sign-in post.
                    </Typography>
                    <TextField
                        label="Dashboard announcement text"
                        value={dashboardAnnouncementText}
                        onChange={(event) => setDashboardAnnouncementText(event.target.value)}
                        inputProps={{ maxLength: 2000 }}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                </Box>

                <Divider />

                <Box component="section" aria-labelledby="student-dashboard-announcement-heading" sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                    <Typography id="student-dashboard-announcement-heading" variant="h6" gutterBottom>Student Dashboard Post</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This post slides into the student dashboard after sign-in. Upload an image or an HTML flyer; optional title and message must be provided together.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={dashboardPostEnabled} onChange={(event) => setDashboardPostEnabled(event.target.checked)} />}
                            label="Show announcement after student login"
                        />
                        <TextField
                            label="Post title (optional)"
                            value={dashboardPostTitle}
                            onChange={(event) => setDashboardPostTitle(event.target.value)}
                            inputProps={{ maxLength: 120 }}
                            fullWidth
                        />
                        <TextField
                            label="Post message (optional)"
                            value={dashboardPostBody}
                            onChange={(event) => setDashboardPostBody(event.target.value)}
                            inputProps={{ maxLength: 2000 }}
                            multiline
                            minRows={3}
                            fullWidth
                        />
                        {Boolean(dashboardPostTitle.trim()) !== Boolean(dashboardPostBody.trim()) && (
                            <Alert severity="info">For a text caption, fill in both the title and message, or leave both blank.</Alert>
                        )}
                        <TextField
                            label="Image URL or site-relative path (optional if using HTML)"
                            value={dashboardPostImageUrl}
                            onChange={(event) => setDashboardPostImageUrl(event.target.value)}
                            error={dashboardPostEnabled && !dashboardPostImageUrl.trim() && !dashboardPostHtmlUrl.trim()}
                            helperText={dashboardPostEnabled && !dashboardPostImageUrl.trim() && !dashboardPostHtmlUrl.trim()
                                ? 'Add an image or HTML flyer before enabling the dashboard post.'
                                : 'Provide an image URL or path only when you are not using an HTML flyer.'}
                            fullWidth
                        />
                        <Button component="label" variant="outlined" disabled={uploadingPostAsset} sx={{ alignSelf: 'flex-start' }}>
                            {uploadingPostAsset ? 'Uploading announcement file…' : 'Upload image or HTML flyer'}
                            <input
                                hidden
                                type="file"
                                aria-label="Announcement image or HTML flyer"
                                accept="image/jpeg,image/png,image/gif,image/webp,.html,text/html"
                                onChange={(event) => {
                                    void handleDashboardPostAssetUpload(event.target.files?.[0]);
                                    event.target.value = '';
                                }}
                            />
                        </Button>
                        <TextField
                            label="HTML flyer URL or site-relative path (optional)"
                            value={dashboardPostHtmlUrl}
                            onChange={(event) => setDashboardPostHtmlUrl(event.target.value)}
                            helperText="Upload a standalone .html flyer or enter an HTTPS/site-relative HTML URL. Local HTTP URLs work only in development. Set flyer links to target=_top; close buttons should postMessage('dashboard-announcement:close', '*') to the parent."
                            fullWidth
                        />
                        {dashboardPostImageUrl.trim() && (
                            <Box component="img" src={dashboardPostImageUrl.trim()} alt="Dashboard announcement preview" sx={{ width: '100%', maxWidth: 480, maxHeight: 300, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                        )}
                        {dashboardPostHtmlUrl.trim() && (
                            <Box
                                component="iframe"
                                src={getDashboardFlyerUrl(dashboardPostHtmlUrl)}
                                title="Dashboard announcement HTML preview"
                                sandbox="allow-scripts allow-top-navigation-by-user-activation"
                                referrerPolicy="no-referrer"
                                sx={{ width: '100%', maxWidth: 480, height: 300, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                            />
                        )}
                        {dashboardPostImageUrl.trim() && dashboardPostHtmlUrl.trim() && (
                            <Alert severity="error">Choose an image or an HTML flyer, not both.</Alert>
                        )}
                        <Button color="error" variant="outlined" onClick={handleDeleteDashboardPost} disabled={saving || uploadingPostAsset || (!dashboardPostEnabled && !dashboardPostImageUrl && !dashboardPostHtmlUrl && !dashboardPostTitle && !dashboardPostBody)} sx={{ alignSelf: 'flex-start' }}>
                            Delete dashboard announcement
                        </Button>
                        <Button variant="contained" onClick={handleSaveDashboardPost} disabled={saving || uploadingPostAsset || (dashboardPostEnabled && !dashboardPostImageUrl.trim() && !dashboardPostHtmlUrl.trim()) || Boolean(dashboardPostImageUrl.trim() && dashboardPostHtmlUrl.trim()) || (Boolean(dashboardPostTitle.trim()) !== Boolean(dashboardPostBody.trim()))} sx={{ alignSelf: 'flex-start' }}>
                            {saving ? 'Saving...' : 'Save dashboard post'}
                        </Button>
                    </Box>
                </Box>

                <Divider />

                <Box component="section" aria-labelledby="referral-program-heading">
                    <Typography id="referral-program-heading" variant="h6" gutterBottom>Referral Program</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure referral rewards and the default welcome discount assigned to new referred users.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(220px, 1fr))' }, gap: 2 }}>
                        <FormControlLabel
                            control={<Switch checked={referralProgram.enabled} onChange={(event) => setReferralProgram((current) => ({ ...current, enabled: event.target.checked }))} />}
                            label="Enable referral discounts and rewards"
                        />
                        <TextField label="Welcome discount (%)" type="number" value={referralProgram.welcomeDiscountPercent} onChange={(event) => setReferralProgram((current) => ({ ...current, welcomeDiscountPercent: Number(event.target.value) }))} inputProps={{ min: 0, max: 100 }} />
                        <TextField label="Welcome discount validity (days)" type="number" value={referralProgram.welcomeDiscountValidityDays} onChange={(event) => setReferralProgram((current) => ({ ...current, welcomeDiscountValidityDays: Number(event.target.value) }))} inputProps={{ min: 1, max: 365 }} />
                        <TextField label="Referral reward (%)" type="number" value={referralProgram.referralRewardPercent} onChange={(event) => setReferralProgram((current) => ({ ...current, referralRewardPercent: Number(event.target.value) }))} inputProps={{ min: 0, max: 100 }} />
                        <TextField label="Referral reward validity (days)" type="number" value={referralProgram.referralRewardValidityDays} onChange={(event) => setReferralProgram((current) => ({ ...current, referralRewardValidityDays: Number(event.target.value) }))} inputProps={{ min: 1, max: 365 }} />
                        <TextField label="Referral milestones (distinct features)" value={referralProgram.milestones.join(', ')} InputProps={{ readOnly: true }} helperText="Fixed milestones: 1, 3, and 5 distinct paid features." />
                    </Box>
                </Box>

                <Divider />

                <Box>
                    <Typography variant="h6" gutterBottom>Individual User Payment Requirements</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Set the required payment amount for the listed premium features. These values are used to determine access eligibility for individual users.
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(220px, 1fr))' }, gap: 2 }}>
                        {[
                            ['jamb', 'Explore JAMB'],
                            ['waec', 'Explore WAEC'],
                            ['jambMockTest', 'JAMB Mock Test'],
                            ['waecMockTest', 'WAEC Mock Test'],
                            ['jambResultCheck', 'Check JAMB Results'],
                            ['waecResultCheck', 'Check WAEC Results'],
                            ['labs', 'Labs'],
                            ['languageHub', 'Language Hub'],
                        ].map(([key, label]) => (
                            <TextField
                                key={key}
                                label={label}
                                type="number"
                                value={individualAccess[key as keyof typeof defaultIndividualAccess] ?? 0}
                                onChange={(e) => updateIndividualAccess(key as keyof typeof defaultIndividualAccess, e.target.value)}
                                InputProps={{ inputProps: { min: 0, step: 1 } }}
                            />
                        ))}
                    </Box>
                </Box>

                <Divider />

                <Box>
                    <Typography variant="h6" gutterBottom>School Subscription Requirement</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        School access for School Work is determined by the number of registered students. This amount represents the per-student cost used to calculate the required school payment.
                    </Typography>
                    <TextField
                        label="Per Student Cost"
                        type="number"
                        value={schoolPerStudentRate}
                        onChange={(e) => setSchoolPerStudentRate(Number(e.target.value || 0))}
                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                        sx={{ maxWidth: 280 }}
                    />
                </Box>

                <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSave} 
                    disabled={saving}
                    sx={{ mt: 2, alignSelf: 'flex-start' }}
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>
            </Box>
        </Paper>
    );
};

export default AdminSiteSettingsTab;
